import { convertTo3wa } from 'src/composables/googleMaps';

export function getUserBrowser() {
  let browserDocumentation = '';
  const userAgent = navigator.userAgent;
  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserDocumentation = 'https://support.google.com/chrome/answer/142065?co=GENIE.Platform%3DDesktop&hl=en';
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserDocumentation = 'https://yandex.com/support/common/browsers-settings/geo-firefox.html';
  } else if (userAgent.match(/safari/i)) {
    browserDocumentation = 'https://support.apple.com/en-us/HT204690';
  } else if (userAgent.match(/opr\//i)) {
    browserDocumentation = 'https://help.opera.com/en/latest/web-preferences/#location';
  } else if (userAgent.match(/edg/i)) {
    browserDocumentation = 'https://support.google.com/chrome/answer/142065?co=GENIE.Platform%3DDesktop&hl=en';
  } else if (userAgent.match(/SamsungBrowser/i)) {
    browserDocumentation =
      'https://www.samsung.com/latin_en/support/mobile-devices/how-to-activate-my-location-and-change-settings-for-location-permissions/#:~:text=Launch%20the%20Settings%20app%2C%20and,enable%20or%20disable%20location%20services.';
  }
  return browserDocumentation;
}

let isApproximateLocation = false;

interface IPLocationData {
  latitude: number;
  longitude: number;
  w3w?: string;
  [key: string]: unknown;
}

interface CachedIPLocation {
  data: IPLocationData;
  timestamp: number;
}

let ipLocationCache: CachedIPLocation | null = null;
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export async function getLocationByIP() {
  try {
    // Check if we have valid cached data
    if (ipLocationCache && Date.now() - ipLocationCache.timestamp < CACHE_DURATION) {
      return [ipLocationCache.data, null];
    }

    const result = await fetch('https://get.geojs.io/v1/ip/geo.json').then((res) => res.json());
    const res = await convertTo3wa(
      new google.maps.LatLng({ lat: Number(result.latitude || 0), lng: Number(result.longitude || 0) })
    ).catch(() => undefined);

    result.w3w = res?.words;

    // Cache the result
    ipLocationCache = {
      data: result,
      timestamp: Date.now(),
    };

    return [result as { latitude: number; longitude: number; w3w?: string }, null] as const;
  } catch (error) {
    return [null, error] as const;
  }
}

export function getPosition(options: PositionOptions) {
  return new Promise((resolve: PositionCallback, reject: PositionErrorCallback) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, options)
  );
}

export async function getLocationInfo(location: google.maps.LatLng) {
  const geoCoder = new google.maps.Geocoder();
  if (typeof location.lat === 'number' || typeof location.lng === 'number') {
    location = new google.maps.LatLng(location);
  }
  const res = await convertTo3wa(location).catch(() => undefined);
  let address: ILocationInfo = {
    latitude: location.lat(),
    longitude: location.lng(),
    w3w: res?.words || '',
    formattedAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
  };
  if (isApproximateLocation) {
    isApproximateLocation = false;
    return address;
  }
  const latLng = new google.maps.LatLng(location.lat(), location.lng());
  const resp = (
    await geoCoder.geocode({
      location: latLng,
    })
  )?.results?.[0];

  address = await formatResult(resp);
  address.latitude = location.lat();
  address.longitude = location.lng();
  return address;
}

type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;

export interface ILocation {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  w3w?: string;
  route?: string;
}

export type ILocationInfo = AtLeast<ILocation, 'latitude' | 'longitude'>;

export async function getLocation(
  defaultZoom = 8,
  defaultLocation: ILocationInfo = { latitude: 0, longitude: 0 }
): Promise<[ILocationInfo, number, boolean]> {
  let locationObject: ILocationInfo;
  try {
    if (!navigator.geolocation) {
      throw new Error('Loaction access not allowed or device is not supported. Using IP based geolocation');
    }
    const coords = (
      await getPosition({
        enableHighAccuracy: false,
        // 10 minutes
        maximumAge: 1000 * 60 * 10,
      })
    ).coords;
    locationObject =
      (await getLocationInfo(new google.maps.LatLng(coords.latitude, coords.longitude))) || defaultLocation;
    return [locationObject, defaultZoom, true];
  } catch (error) {
    const [ipLocation, err] = await getLocationByIP();
    if (!ipLocation?.latitude || !ipLocation?.longitude || err) {
      locationObject = defaultLocation;
      defaultZoom = 8;
      return [locationObject, defaultZoom, false];
    }
    locationObject =
      (await getLocationInfo(new google.maps.LatLng(Number(ipLocation.latitude), Number(ipLocation.longitude)))) ||
      defaultLocation;
    defaultZoom = 8;

    return [locationObject, defaultZoom, false];
  }
}

export async function zoomControl(controlDiv: HTMLDivElement, map: google.maps.Map) {
  if (typeof window === 'undefined') return;
  controlDiv.style.margin = '0px 24px 18px 0px';

  // Set CSS for the control wrapper
  const controlWrapper = document.createElement('div');
  controlWrapper.style.backgroundColor = 'white';
  controlWrapper.style.borderRadius = '20px';
  controlWrapper.style.cursor = 'pointer';
  controlWrapper.style.textAlign = 'center';
  controlWrapper.style.width = '40px';
  controlWrapper.style.height = '80px';
  controlDiv.appendChild(controlWrapper);

  // Set CSS for the zoomIn
  const zoomInButton = document.createElement('img');
  zoomInButton.width = 40;
  zoomInButton.height = 40;
  zoomInButton.style.padding = '7px';
  zoomInButton.src = (await import('../components/claims-widget/assets/zoomin.svg')).default;
  zoomInButton.style.borderBottom = 'solid 1px #F4F4F4';
  controlWrapper.appendChild(zoomInButton);

  // Set CSS for the zoomOut
  const zoomOutButton = document.createElement('img');
  zoomOutButton.width = 40;
  zoomOutButton.height = 40;
  zoomOutButton.style.padding = '7px';
  zoomOutButton.src = (await import('../components/claims-widget/assets/zoomout.svg')).default;
  controlWrapper.appendChild(zoomOutButton);

  // Setup the click event listener - zoomIn
  zoomInButton.addEventListener('click', () => {
    const actualZoom = map?.getZoom();
    if (!actualZoom) {
      return;
    }
    map.setZoom(actualZoom + 1);
  });

  // Setup the click event listener - zoomOut
  zoomOutButton.addEventListener('click', () => {
    const actualZoom = map?.getZoom();
    if (!actualZoom) {
      return;
    }
    map.setZoom(actualZoom - 1);
  });
}

export async function addYourLocationButton(controlDiv: HTMLDivElement, map: google.maps.Map) {
  if (typeof window === 'undefined') return;

  controlDiv.style.margin = '0px 24px 18px 0px';

  // Set CSS for the control button
  const controlButton = document.createElement('button');
  controlButton.style.border = 'none';
  controlButton.style.outline = 'none';
  controlButton.style.backgroundColor = '#fff';
  controlButton.style.width = '40px';
  controlButton.style.height = '40px';
  controlButton.style.borderRadius = '20px';
  controlButton.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
  controlButton.style.cursor = 'pointer';
  controlButton.style.padding = '0';
  controlButton.title = 'Your Location';
  controlDiv.appendChild(controlButton);

  // Set CSS for the icon
  const icon = document.createElement('img');
  icon.width = 40;
  icon.height = 40;
  icon.style.padding = '7px';
  icon.src = (await import('../components/claims-widget/assets/currentlocation.svg')).default;
  controlButton.appendChild(icon);

  // Setup the click event listener - move
  controlButton.addEventListener('click', async () => {
    const [location] = await getLocation();
    map.panTo(new google.maps.LatLng(location.latitude, location.longitude));
    map.setZoom(16);
  });
}

export function getFormatValue(
  place: google.maps.places.PlaceResult,
  type: string | string[],
  name: 'short_name' | 'long_name'
) {
  const component = place.address_components?.find((component) =>
    component.types.some((componentType) => type.includes(componentType))
  );
  return component?.[name] ?? '';
}

export async function formatResult(place: google.maps.places.PlaceResult & { words?: string }) {
  const returnData: ILocationInfo = {
    latitude: 0,
    longitude: 0,
    formattedAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    route: '',
    w3w: place.words || '',
  };
  if (!place.address_components) {
    return returnData;
  }

  const street_number = getFormatValue(place, 'street_number', 'short_name');
  const route = getFormatValue(place, ['route', 'establishment'], 'long_name');
  returnData.addressLine1 = street_number ? `${street_number} ${route}` : route || '';
  returnData.addressLine2 = getFormatValue(place, 'subpremise', 'short_name');
  returnData.city = getFormatValue(place, ['postal_town', 'locality', 'sublocality'], 'long_name');
  returnData.state = getFormatValue(
    place,
    ['administrative_area_level_2', 'administrative_area_level_1', 'administrative_area_level_3'],
    'long_name'
  );
  returnData.country = getFormatValue(place, 'country', 'long_name');
  returnData.postalCode = getFormatValue(place, ['postalCode', 'postal_code', 'post_box', 'plus_code'], 'short_name');
  returnData.route = getFormatValue(place, 'route', 'long_name');

  if (!place?.geometry?.location) {
    return returnData;
  }
  returnData.latitude = place.geometry.location.lat();
  returnData.longitude = place.geometry.location.lng();
  if (!returnData?.w3w) {
    const res = await convertTo3wa(place.geometry.location).catch(() => undefined);
    returnData.w3w = res?.words;
  }
  returnData.formattedAddress = place.formatted_address ?? '';
  return returnData;
}
